import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UsersRound, Copy, Trash2, Send, Clock } from "lucide-react";
import { API } from "@/lib/api";

const ROLE_LABELS = { admin: "Admin", mitarbeiter: "Mitarbeiter", steuerberater: "Steuerberater (nur lesen)" };

export const TeamCard = ({ hasFeature }) => {
  const enabled = hasFeature("multi_user");
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("mitarbeiter");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");
  const [advisers, setAdvisers] = useState([]);
  const [adviserEmail, setAdviserEmail] = useState("");
  const [adviserNote, setAdviserNote] = useState("");

  const load = useCallback(async () => {
    try {
      const [team, access] = await Promise.all([
        axios.get(`${API}/team/members`),
        axios.get(`${API}/workspaces/current/advisers`).catch(() => ({ data: { advisers: [] } })),
      ]);
      setData(team.data);
      setAdvisers(access.data?.advisers || []);
    } catch {
      setData(null);
    }
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(
      () => toast.success("Einladungslink kopiert"),
      () => toast.error("Kopieren fehlgeschlagen")
    );
  };

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error("Bitte E-Mail-Adresse angeben"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/team/invite`, {
        email: inviteEmail.trim(), role: inviteRole, origin_url: window.location.origin,
      });
      setInviteEmail("");
      setLastLink(r.data.invite_url || "");
      if (r.data.email_sent) {
        toast.success("Einladung per E-Mail versendet");
      } else {
        toast.success("Einladung erstellt – Link kopieren und weitergeben (E-Mail-Versand nicht konfiguriert)");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (id) => {
    try {
      await axios.delete(`${API}/team/invites/${id}`);
      toast.success("Einladung widerrufen");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`${m.email} wirklich aus dem Team entfernen? Der Zugang wird sofort gelöscht.`)) return;
    try {
      await axios.delete(`${API}/team/members/${m.id}`);
      toast.success("Mitglied entfernt");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const changeRole = async (m, role) => {
    try {
      await axios.put(`${API}/team/members/${m.id}/role`, { role });
      toast.success("Rolle geändert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const saveCommission = async (m, val) => {
    const rate = Math.min(100, Math.max(0, Number(val) || 0));
    if (rate === (m.commission_rate ?? 0)) return;
    try {
      await axios.put(`${API}/team/members/${m.id}/commission`, { rate });
      toast.success(`Provisionssatz: ${rate} %`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const grantAdviser = async () => {
    if (!adviserEmail.trim()) { toast.error("Bitte E-Mail-Adresse angeben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/workspaces/current/advisers`, { email: adviserEmail.trim(), note: adviserNote.trim() });
      setAdviserEmail(""); setAdviserNote("");
      toast.success("Betreuerzugang freigegeben");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Betreuerzugang konnte nicht freigegeben werden"); }
    finally { setBusy(false); }
  };

  const revokeAdviser = async (id) => {
    if (!window.confirm("Diesen externen Betreuerzugang wirklich widerrufen?")) return;
    try { await axios.delete(`${API}/workspaces/current/advisers/${id}`); toast.success("Betreuerzugang widerrufen"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Widerruf fehlgeschlagen"); }
  };

  const isAdmin = (data?.my_role || "admin") === "admin";

  return (
    <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-team-card">
      <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1 flex items-center gap-2">
        <UsersRound className="w-5 h-5 text-[var(--brand-primary)]" /> Team &amp; Mitarbeiter
      </h2>
      <p className="text-sm text-[var(--brand-muted)] mb-4">
        Lade Vertrieb, Buchhaltung oder Kundenservice ein. <b>Steuerberater</b> erhalten einen serverseitig schreibgeschützten Zugang für Auswertungen und Exporte; sie können keine Geschäftsdaten, Einstellungen oder Zahlungen verändern.
      </p>

      {!enabled ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="team-upsell">
          Die Team-Funktion (Multi-User) ist im <b>Business-Tarif</b> enthalten. Upgrade unter „Mein Abo“, um Mitarbeiter einzuladen.
        </div>
      ) : (
        <>
          {isAdmin && (
            <div className="rounded-lg border border-[var(--brand-border-light)] p-4 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_170px_auto] gap-3 items-end">
                <div>
                  <Label>E-Mail-Adresse</Label>
                  <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="kollege@firma.de" data-testid="team-invite-email" />
                </div>
                <div>
                  <Label>Rolle</Label>
                  <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full h-10 border border-[var(--brand-border)] rounded-md px-2 text-sm bg-white"
                    data-testid="team-invite-role">
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="steuerberater">Steuerberater (nur lesen)</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <Button onClick={invite} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="team-invite-btn">
                  <Send className="w-4 h-4 mr-1" /> {busy ? "Sendet …" : "Einladen"}
                </Button>
              </div>
              {lastLink && (
                <div className="mt-3 flex items-center gap-2 text-xs text-[var(--brand-muted)]">
                  <span className="truncate max-w-[360px]" data-testid="team-last-link">{lastLink}</span>
                  <button onClick={() => copyLink(lastLink)} className="text-[var(--brand-primary)] hover:underline flex items-center gap-1 shrink-0" data-testid="team-copy-last-link">
                    <Copy className="w-3 h-3" /> Link kopieren
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5" data-testid="team-members-list">
            {(data?.members || []).map((m) => (
              <div key={m.id} className="flex items-center gap-3 rounded-lg border border-[var(--brand-border-light)] px-3 py-2" data-testid={`team-member-${m.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[var(--brand-ink)] truncate">
                    {m.name || m.email} {m.me && <span className="text-[11px] text-[var(--brand-muted)]">(du)</span>}
                  </div>
                  <div className="text-[11px] text-[var(--brand-muted)] truncate">{m.email}</div>
                </div>
                {isAdmin && (
                  <span className="flex items-center gap-1 text-xs text-[var(--brand-muted)] shrink-0" title="Provisionssatz in % (auf gewonnene Deals)">
                    <input type="number" min="0" max="100" defaultValue={m.commission_rate ?? 0}
                      onBlur={(e) => saveCommission(m, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="w-14 h-8 border border-[var(--brand-border)] rounded px-1.5 text-xs"
                      data-testid={`team-member-commission-${m.email}`} />%
                  </span>
                )}
                {isAdmin && !m.me ? (
                  <select value={m.role} onChange={(e) => changeRole(m, e.target.value)}
                    className="h-8 border border-[var(--brand-border)] rounded px-1.5 text-xs bg-white shrink-0"
                    data-testid={`team-member-role-${m.email}`}>
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="steuerberater">Steuerberater (nur lesen)</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[var(--brand-border-light)] text-[var(--brand-ink)] shrink-0">
                    {ROLE_LABELS[m.role] || m.role}
                  </span>
                )}
                {isAdmin && !m.me && (
                  <button onClick={() => removeMember(m)} className="text-red-500 hover:text-red-700 shrink-0" title="Entfernen" data-testid={`team-member-remove-${m.email}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="mt-5 border-t border-[var(--brand-border-light)] pt-4" data-testid="external-adviser-access">
              <div className="text-sm font-semibold text-[var(--brand-ink)]">Externe Betreuer</div>
              <p className="text-xs text-[var(--brand-muted)] mt-1">Gib einer bereits registrierten Person gezielt Zugriff auf diesen Workspace. Sie nutzt immer ihren eigenen Login und kann den Workspace später oben in der App wechseln.</p>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end mt-3">
                <div><Label>E-Mail des Betreuers</Label><Input value={adviserEmail} onChange={(e) => setAdviserEmail(e.target.value)} placeholder="betreuung@partner.de" data-testid="adviser-email" /></div>
                <div><Label>Hinweis (optional)</Label><Input value={adviserNote} onChange={(e) => setAdviserNote(e.target.value)} placeholder="z. B. CRM-Betreuung" data-testid="adviser-note" /></div>
                <Button onClick={grantAdviser} disabled={busy} variant="outline" className="border-[var(--brand-primary)] text-[var(--brand-ink)]" data-testid="adviser-grant-btn">Freigeben</Button>
              </div>
              {advisers.length > 0 && <div className="mt-3 space-y-1.5">{advisers.map((item) => <div key={item.id} className="flex items-center gap-3 rounded-lg border border-[var(--brand-border-light)] px-3 py-2"><div className="min-w-0 flex-1"><div className="text-sm font-medium text-[var(--brand-ink)] truncate">{item.name || item.email}</div><div className="text-xs text-[var(--brand-muted)] truncate">{item.email}{item.note ? ` · ${item.note}` : ""}</div></div><button onClick={() => revokeAdviser(item.id)} className="text-red-500 hover:text-red-700 shrink-0" title="Zugang widerrufen"><Trash2 className="w-4 h-4" /></button></div>)}</div>}
            </div>
          )}

          {(data?.invites || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--brand-muted)] font-semibold mb-1.5">Ausstehende Einladungen</div>
              <div className="space-y-1.5" data-testid="team-invites-list">
                {data.invites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 rounded-lg border border-dashed border-[var(--brand-border)] px-3 py-2" data-testid={`team-invite-${inv.email}`}>
                    <Clock className="w-4 h-4 text-[var(--brand-muted)] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[var(--brand-ink)] truncate">{inv.email}</div>
                      <div className="text-[11px] text-[var(--brand-muted)]">{ROLE_LABELS[inv.role] || inv.role} · gültig bis {new Date(inv.expires_at).toLocaleDateString("de-DE")}</div>
                    </div>
                    <button onClick={() => copyLink(`${window.location.origin}/invite/${inv.token}`)} className="text-[var(--brand-primary)] hover:underline text-xs flex items-center gap-1 shrink-0" data-testid={`team-invite-copy-${inv.email}`}>
                      <Copy className="w-3 h-3" /> Link
                    </button>
                    {isAdmin && (
                      <button onClick={() => revokeInvite(inv.id)} className="text-red-500 hover:text-red-700 shrink-0" title="Widerrufen" data-testid={`team-invite-revoke-${inv.email}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};
